<template>
  <f7-page name="paymentLinkPage">
    <f7-navbar :title="$t('common.payment')" back-link />

    <div class="avocash-checkout-page p-4">
      <f7-block v-if="loading" class="text-align-center py-8">
        <f7-preloader color="green" />
        <p class="font-body-md text-on-surface-variant mt-2">{{ $t('common.loading') }}</p>
      </f7-block>

      <f7-block v-else-if="errorMessage" class="bg-surface-container-lowest p-6 rounded-2xl shadow-sm text-align-center">
        <f7-icon material="error" :size="48" class="text-red mb-2" />
        <p class="font-body-md text-on-surface font-semibold">{{ errorMessage }}</p>
      </f7-block>

      <f7-block v-else-if="needsLogin" class="bg-surface-container-lowest p-6 rounded-2xl shadow-sm text-align-center space-y-4">
        <f7-icon material="lock_person" :size="60" class="text-primary" />
        <h2 class="font-headline-md text-on-surface font-bold">{{ link?.title }}</h2>
        <p class="font-body-md text-on-surface-variant">{{ $t('common.paymentLinkLoginRequired') }}</p>
        <f7-button
          fill
          raised
          class="width-100 py-3.5 rounded-xl font-label-lg font-semibold display-flex align-items-center justify-content-center gap-2"
          @click="login"
        >
          <f7-icon material="login" :size="20" />
          {{ $t('common.login') }}
        </f7-button>
      </f7-block>

      <div v-else-if="link" class="space-y-4 max-w-lg mx-auto">
        <!-- Status & Order Summary Card -->
        <f7-card class="payment-summary-card" padding="false">
          <f7-card-content class="payment-summary-content">
            <div class="display-flex align-items-center justify-content-space-between pb-2">
              <f7-badge color="primary" class="display-flex align-items-center gap-1">
                <f7-icon material="verified_user" :size="14" />
                {{ $t('common.sslSecureGateway') }}
              </f7-badge>
              <span class="font-label-sm text-outline">Réf: AVO-TXN-{{ link.id || 'PAY' }}</span>
            </div>
            <div class="mt-2">
              <span v-if="link.targetType" class="display-inline-block bg-primary/10 text-primary font-label-sm px-2.5 py-0.5 rounded-full mb-1">
                {{ link.targetType }}
              </span>
              <h2 class="font-headline-sm text-on-surface font-bold line-clamp-1">{{ link.title }}</h2>
              <p v-if="link.description" class="font-body-sm text-on-surface-variant mt-1">{{ link.description }}</p>
            </div>
            <div class="mt-4 pt-3 bg-surface-container-low rounded-xl p-4 display-flex align-items-baseline justify-content-space-between">
              <div>
                <span class="font-label-sm text-outline uppercase tracking-wider">{{ $t('common.amountToPay') }}</span>
                <div class="font-headline-xl text-primary font-bold">
                  {{ money(link.amount * (form.quantity || 1), link.currency) }}
                </div>
              </div>
              <div class="text-align-right">
                <f7-badge color="primary" class="display-flex align-items-center gap-1 font-label-sm bg-surface-container-lowest px-2.5 py-1 rounded-full shadow-xs">
                  <f7-icon material="check_circle" :size="14" />
                  {{ $t('common.zeroFees', { currency: link.currency }) }}
                </f7-badge>
              </div>
            </div>
          </f7-card-content>
        </f7-card>

        <!-- Method Tabs -->
        <f7-segmented class="payment-method-tabs" :value="selectedMethod" @change="selectedMethod = $event.target.value">
          <f7-button value="momo" class="display-flex align-items-center justify-content-center gap-1.5">
            <f7-icon material="smartphone" :size="18" />
            <span>{{ $t('common.mobileMoney') }}</span>
          </f7-button>
          <f7-button value="stripe" class="display-flex align-items-center justify-content-center gap-1.5">
            <f7-icon material="credit_card" :size="18" />
            <span>{{ $t('common.cardInfo') }}</span>
          </f7-button>
          <f7-button value="qr" class="display-flex align-items-center justify-content-center gap-1.5">
            <f7-icon material="qr_code_2" :size="18" />
            <span>{{ $t('common.qrCode') }}</span>
          </f7-button>
        </f7-segmented>

        <!-- Mobile Money Panel -->
        <div v-if="selectedMethod === 'momo'" class="payment-panel bg-surface-container-lowest p-4 rounded-2xl shadow-sm space-y-4">
          <label class="display-block font-label-md text-on-surface-variant">{{ $t('common.selectOperator') }}</label>
          <f7-segmented class="operator-segmented" :value="selectedOperator" @change="selectedOperator = $event.target.value">
            <f7-button value="wave" class="display-flex flex-direction-column align-items-center justify-content-center py-3 px-2">
              <f7-icon material="waves" :size="24" />
              <span class="font-label-md mt-1 font-semibold">{{ $t('common.wave') }}</span>
            </f7-button>
            <f7-button value="orange" class="display-flex flex-direction-column align-items-center justify-content-center py-3 px-2">
              <f7-icon material="signal_cellular_alt" :size="24" />
              <span class="font-label-md mt-1 font-semibold">{{ $t('common.orange') }}</span>
            </f7-button>
            <f7-button value="mtn" class="display-flex flex-direction-column align-items-center justify-content-center py-3 px-2">
              <f7-icon material="bolt" :size="24" />
              <span class="font-label-md mt-1 font-semibold">{{ $t('common.mtnMoMo') }}</span>
            </f7-button>
          </f7-segmented>

          <div class="space-y-3 pt-2">
            <f7-list-input
              :label="$t('common.quantity')"
              type="number"
              min="1"
              v-model="form.quantity"
              class="avocash-input"
            />
            <f7-list-input
              :label="$t('common.email')"
              type="email"
              :placeholder="$t('paymentLink.emailPlaceholder')"
              v-model="form.customerEmail"
              class="avocash-input"
            />
          </div>
        </div>

        <!-- Stripe Credit Card Panel -->
        <div v-else-if="selectedMethod === 'stripe'" class="payment-panel bg-surface-container-lowest p-4 rounded-2xl shadow-sm space-y-3">
          <div class="display-flex align-items-center justify-content-space-between">
            <span class="font-label-md text-on-surface-variant">{{ $t('common.cardInfo') }}</span>
            <f7-badge color="primary" class="display-flex align-items-center gap-1 font-label-sm">
              <f7-icon material="verified" :size="14" />
              {{ $t('common.stripeVerified') }}
            </f7-badge>
          </div>
          <f7-list-input
            :label="$t('common.email')"
            type="email"
            :placeholder="$t('paymentLink.emailPlaceholder')"
            v-model="form.customerEmail"
            class="avocash-input"
          />
        </div>

        <!-- QR Code Panel -->
        <div v-else-if="selectedMethod === 'qr'" class="payment-panel bg-surface-container-lowest p-6 rounded-2xl shadow-sm text-align-center space-y-3">
          <img v-if="link.qrUrl" :src="link.qrUrl" :alt="$t('common.a11y.paymentLinkQr')" class="w-40 h-40 mx-auto rounded-xl border p-2" />
          <div v-else class="w-40 h-40 mx-auto rounded-xl bg-surface-container display-flex align-items-center justify-content-center">
            <span class="material-symbols-outlined text-primary" style="font-size: 60px;">qr_code_2</span>
          </div>
          <p class="font-body-sm text-on-surface-variant">{{ $t('common.scanQrCodeToPay') }}</p>
        </div>

        <!-- Financial Breakdown -->
        <f7-card class="payment-breakdown" padding="false">
          <f7-card-content class="payment-breakdown-content space-y-2">
            <div class="display-flex justify-content-space-between align-items-center text-body-sm text-on-surface-variant">
              <span>{{ $t('common.subtotal') }}</span>
              <span class="font-semibold text-on-surface">{{ money(link.amount * (form.quantity || 1), link.currency) }}</span>
            </div>
            <div class="display-flex justify-content-space-between align-items-center text-body-sm text-on-surface-variant">
              <span>{{ $t('common.infrastructureFees') }}</span>
              <span class="text-primary font-semibold">{{ $t('common.free', { currency: link.currency }) }}</span>
            </div>
            <div class="pt-2 border-t display-flex justify-content-space-between align-items-center font-headline-sm text-on-surface">
              <span class="font-bold">{{ $t('common.netTotal') }}</span>
              <span class="text-primary font-bold">{{ money(link.amount * (form.quantity || 1), link.currency) }}</span>
            </div>
          </f7-card-content>
        </f7-card>

        <!-- Trigger Button -->
        <f7-button
          fill
          raised
          class="width-100 py-4 rounded-xl font-headline-sm font-semibold display-flex align-items-center justify-content-center gap-2 shadow-md"
          :disabled="paying"
          @click="pay"
        >
          <f7-icon material="lock" :size="20" />
          <span>{{ paying ? $t('common.loading') : $t('common.continueToPayment') }}</span>
        </f7-button>
        <div class="display-flex align-items-center justify-content-center gap-1.5 text-outline font-label-sm text-align-center">
          <f7-icon material="security" :size="15" class="text-primary" />
          <span>{{ $t('common.bankEncryptionPci') }}</span>
        </div>
      </div>
    </div>
  </f7-page>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { Capacitor } from '@capacitor/core';
import { usePaymentLinkStore } from '../js/pinia-stores/payment_link.js';
import { useKeycloakStore } from '../js/pinia-stores/keycloak.js';
import i18n from '@/js/i18n.js';

const props = defineProps({
  path: { type: String, required: true },
  slug: { type: String, default: '' },
  token: { type: String, default: '' },
});

const realSlug = computed(() => props.slug || props.token);

const paymentLinkStore = usePaymentLinkStore();
const keycloakStore = useKeycloakStore();
const link = ref(null);
const loading = ref(true);
const paying = ref(false);
const errorMessage = ref('');
const form = reactive({ quantity: 1, customerEmail: '' });
const selectedMethod = ref('momo');
const selectedOperator = ref('wave');

const needsLogin = computed(
  () =>
    link.value != null &&
    (props.path === 'i' ||
      link.value.requiresCampaignJoin === true ||
      link.value.requiresAuthentication === true) &&
    !keycloakStore.authenticated,
);

onMounted(async () => {
  try {
    link.value = await paymentLinkStore.previewPaymentLink(props.path, realSlug.value);
  } catch (e) {
    errorMessage.value = i18n.global.t('common.paymentLinkUnavailable');
  } finally {
    loading.value = false;
  }
});

function login() {
  if (Capacitor.isNativePlatform()) {
    keycloakStore.login({
      redirectUri: "avocash://login" + window.location.pathname,
    });
  } else {
    keycloakStore.login();
  }
}

async function pay() {
  paying.value = true;
  try {
    const session = await paymentLinkStore.checkoutPaymentLink(props.path, realSlug.value, {
      ...form,
      quantity: Number(form.quantity),
    });
    window.location.assign(session.providerCheckoutUrl);
  } catch (e) {
    errorMessage.value = i18n.global.t('common.paymentLinkUnavailable');
    paying.value = false;
  }
}

function money(v, c) {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: c || 'XOF' }).format(v || 0);
}
</script>

<style scoped>
.avocash-checkout-page {
  font-family: 'Work Sans', sans-serif;
}

/* Payment Summary Card */
.payment-summary-card {
  margin-bottom: 16px;
  overflow: hidden;
}
.payment-summary-content {
  position: relative;
  z-index: 10;
  padding: 20px;
  background: linear-gradient(135deg, var(--avocash-green-light, #e8f0fe) 0%, var(--avocash-secondary-light, #fce4ec) 100%);
}
.payment-summary-content::before {
  content: '';
  position: absolute;
  right: -24px;
  top: -24px;
  width: 96px;
  height: 96px;
  background: var(--avocash-primary, #0009ff);
  opacity: 0.05;
  border-radius: 50%;
  pointer-events: none;
}

/* Method Tabs */
.payment-method-tabs {
  --f7-button-height: 48px;
  --f7-button-border-radius: 12px;
  --f7-button-font-size: 14px;
  --f7-button-font-weight: 500;
  --f7-segmented-raised-divider-color: transparent;
  margin-bottom: 16px;
  background: var(--avocash-surface-container, #f5f7f2);
  padding: 4px;
  border-radius: 16px;
}
.payment-method-tabs .button {
  transition: all 0.2s ease;
}
.payment-method-tabs .button-active {
  --f7-button-bg-color: var(--f7-card-bg-color, #fff);
  --f7-button-text-color: var(--avocash-primary, #0009ff);
  box-shadow: 0 2px 8px rgba(0,9,255,0.15);
}

/* Operator Segmented */
.operator-segmented {
  --f7-button-height: auto;
  --f7-button-border-radius: 12px;
  --f7-button-font-size: 12px;
  --f7-button-font-weight: 600;
  --f7-segmented-raised-divider-color: transparent;
  background: var(--avocash-surface-container, #f5f7f2);
  padding: 4px;
  border-radius: 16px;
}
.operator-segmented .button {
  border: 1px solid transparent;
  transition: all 0.2s ease;
}
.operator-segmented .button-active {
  --f7-button-bg-color: var(--avocash-primary-container, #e8f0fe);
  --f7-button-text-color: var(--avocash-primary, #0009ff);
  border-color: var(--avocash-primary, #0009ff);
}

/* Payment Panels */
.payment-panel {
  margin-bottom: 16px;
}

/* Breakdown Card */
.payment-breakdown {
  margin-bottom: 16px;
}
.payment-breakdown-content {
  padding: 16px;
}

/* Form Inputs */
.avocash-input {
  width: 100%;
  padding: 10px 14px;
  background-color: var(--avocash-surface-container-low, #edf6ed);
  border: 1px solid var(--f7-list-item-border-color, #bfc9bf);
  border-radius: 10px;
  font-size: 15px;
  color: var(--avocash-on-surface, #161d18);
  outline: none;
}
.avocash-input:focus {
  border-color: var(--avocash-primary, #0009ff);
}

/* Badge adjustments */
.badge-inline {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
</style>